import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Button,
  ConfirmDeleteModal,
  SearchInput,
  SkeletonText,
  TableChip,
  Pagination,
  SideDrawer,
} from '@/components';
import { parseApiError } from '@/common/utils';
import { V2Table } from '@/common/components';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '../../components/Icons';
import {
  useQuotations,
  useDeleteQuotation,
  useQuotationsStateSnapshot,
} from '../hooks';
import { QuotationPdfActions } from './QuotationPdfActions';
import { QuotationForm } from './QuotationForm';
import { QuotationTemplateSelectModal } from './QuotationTemplateSelectModal';
import { DEFAULT_PDF_TEMPLATE_KEY } from '../utils';

export function QuotationsTemplatesTab({
  companyId,
  canCreate,
  canEdit = true,
  canDelete,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    readSnapshot,
    saveTemplatesState,
    attachScrollListener,
    restoreScroll,
  } = useQuotationsStateSnapshot();
  const initial = useRef(readSnapshot().templates).current;

  const [search, setSearch] = useState(initial.search || '');
  const [page, setPage] = useState(initial.page || 1);
  const [deletingId, setDeletingId] = useState(null);
  const [isTemplateSelectOpen, setIsTemplateSelectOpen] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(
    DEFAULT_PDF_TEMPLATE_KEY,
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);

  useEffect(() => {
    saveTemplatesState({ search, page });
  }, [search, page, saveTemplatesState]);

  useEffect(() => {
    return attachScrollListener('templates');
  }, [attachScrollListener]);

  // Auto-open the create form when navigated from the PDF Templates page
  // e.g. /quotations?create=1&template=modern_corporate
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === '1';
    const templateKey = searchParams.get('template');
    if (shouldCreate) {
      if (templateKey) setSelectedTemplateKey(templateKey);
      setIsCreateOpen(true);
      // Clean the URL so refreshing doesn't re-open the drawer while preserving other params like tab
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('create');
          next.delete('template');
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = { page, ...(search ? { search } : {}) };

  const { data, isLoading, isFetching } = useQuotations(companyId, params, {
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useDeleteQuotation();
  const isTableLoading = (isLoading && !data) || isFetching;

  const quotations = Array.isArray(data) ? data : data?.results || [];
  const totalPages = data?.total_pages || data?.num_pages || 1;

  useEffect(() => {
    if (!isLoading && data) {
      restoreScroll('templates');
    }
  }, [isLoading, data, restoreScroll]);

  const openDetail = (id) => navigate(`/quotations/${id}`);

  const columns = [
    {
      id: 'title',
      label: 'Title',
      defaultWidth: 220,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-28" height="h-4" />,
      cell: (q) => (
        <div className="min-w-0">
          <span className="block truncate font-semibold">{q.title}</span>
          {q.template_key && (
            <span className="block text-[11px] font-normal text-text-muted mt-0.5 truncate">
              PDF: {String(q.template_key).replace(/_/g, ' ')}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'code',
      label: 'Quote Code',
      defaultWidth: 140,
      minWidth: 110,
      resizable: true,
      skeleton: <SkeletonText width="w-20" height="h-4" />,
      cell: (q) => (
        <span className="text-xs text-text-muted">{q.quote_code || '—'}</span>
      ),
    },
    {
      id: 'currency',
      label: 'Currency',
      defaultWidth: 100,
      minWidth: 80,
      resizable: true,
      skeleton: <SkeletonText width="w-12" height="h-4" />,
      cell: (q) => q.currency || 'INR',
    },
    {
      id: 'tax',
      label: 'Tax Rate',
      defaultWidth: 100,
      minWidth: 80,
      resizable: true,
      skeleton: <SkeletonText width="w-12" height="h-4" />,
      cell: (q) =>
        q.tax_rate != null && q.tax_rate !== '' ? `${q.tax_rate}%` : '0%',
    },
    {
      id: 'products',
      label: 'Products',
      defaultWidth: 100,
      minWidth: 80,
      resizable: true,
      skeleton: <SkeletonText width="w-8" height="h-4" />,
      cell: (q) => (Array.isArray(q.products) ? q.products.length : '—'),
    },
    {
      id: 'pdf',
      label: 'PDF',
      defaultWidth: 110,
      minWidth: 90,
      resizable: false,
      skeleton: <SkeletonText width="w-16" height="h-5" />,
      cell: (q) => (
        <TableChip tone={q.pdf_ready ? 'success' : 'neutral'}>
          {q.pdf_ready ? 'Ready' : 'Pending'}
        </TableChip>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 110,
      minWidth: 90,
      resizable: false,
      skeleton: <SkeletonText width="w-16" height="h-5" />,
      cell: (q) => (
        <TableChip tone={q.is_active ? 'success' : 'neutral'}>
          {q.is_active ? 'Active' : 'Inactive'}
        </TableChip>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultWidth: 260,
      minWidth: 248,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-24" height="h-5" className="mx-auto" />,
      cell: (q) => (
        <div className="flex items-center justify-end gap-0.5 shrink-0">
          <QuotationPdfActions companyId={companyId} quotation={q} compact />
          <button
            type="button"
            className="p-1.5 rounded-full hover:bg-primary/10 text-primary transition-colors flex items-center justify-center"
            onClick={() => openDetail(q.id)}
            title="View"
          >
            <EyeIcon className="w-[18px] h-[18px]" />
          </button>
          {canEdit && (
            <button
              type="button"
              className="p-1.5 rounded-full hover:bg-primary/10 text-primary transition-colors flex items-center justify-center"
              onClick={() => setEditingQuotation(q)}
              title="Edit"
            >
              <PencilIcon className="w-[18px] h-[18px]" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="p-1.5 rounded-full hover:bg-status-error/10 text-status-error transition-colors flex items-center justify-center"
              onClick={() => setDeletingId(q.id)}
              title="Delete"
            >
              <TrashIcon className="w-[18px] h-[18px]" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleDelete = () => {
    if (!deletingId) return;
    deleteMutation.mutate(
      { companyId, quotationId: deletingId },
      {
        onSuccess: () => {
          toast.success('Quotation deleted.');
          setDeletingId(null);
        },
        onError: (err) => {
          toast.error(parseApiError(err).message || 'Failed to delete.');
          setDeletingId(null);
        },
      },
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden w-full">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 w-full shrink-0">
        <SearchInput
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search by Name"
          id="quotation-search"
          className="w-full sm:max-w-md flex-grow"
        />
        {canCreate && (
          <Button
            variant="primary"
            icon={<PlusIcon className="w-4 h-4" />}
            iconPosition="start"
            onClick={() => setIsTemplateSelectOpen(true)}
            className="w-full sm:w-auto h-[40px] px-4 shrink-0"
          >
            New Quotation
          </Button>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <V2Table
          tight
          columns={columns}
          data={quotations}
          rowKey="id"
          storageKey="quotation_templates_table_column_widths"
          loading={isTableLoading}
          loadingRowCount={8}
          onRowClick={(q) => openDetail(q.id)}
          empty={
            search
              ? 'No quotations match your search.'
              : 'No quotations yet. Click "New Quotation" to get started.'
          }
        />
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex justify-end shrink-0">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={!!deletingId}
        onCancel={() => setDeletingId(null)}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
        title="Delete Quotation"
        description="All linked products will also be removed. This cannot be undone."
        itemName="this quotation"
      />

      <QuotationTemplateSelectModal
        isOpen={isTemplateSelectOpen}
        onClose={() => setIsTemplateSelectOpen(false)}
        onSelect={(templateKey) => {
          setSelectedTemplateKey(templateKey);
          setIsTemplateSelectOpen(false);
          setIsCreateOpen(true);
        }}
        companyId={companyId}
        selectedKey={selectedTemplateKey}
      />

      <SideDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        className="q-drawer"
        title="Create Quotation"
        width="lg"
      >
        <QuotationForm
          companyId={companyId}
          initialTemplateKey={selectedTemplateKey}
          onCancel={() => setIsCreateOpen(false)}
          onSuccess={(data) => {
            setIsCreateOpen(false);
            if (data?.id) navigate(`/quotations/${data.id}`);
          }}
        />
      </SideDrawer>

      <SideDrawer
        isOpen={!!editingQuotation}
        onClose={() => setEditingQuotation(null)}
        className="q-drawer"
        title="Edit Quotation"
        width="lg"
      >
        {editingQuotation && (
          <QuotationForm
            companyId={companyId}
            quotation={editingQuotation}
            onCancel={() => setEditingQuotation(null)}
            onSuccess={() => {
              setEditingQuotation(null);
            }}
          />
        )}
      </SideDrawer>
    </div>
  );
}
